/** The control plane: what a platform operator can do, as React Query hooks.
 *  An operator is not a user of this app: no `person` row, so no organization, tier or abilities, and `GET /api/auth/me` refuses them. Everything hangs off `/api/platform/*`, which resolves the token against `platform_operator`.
 *  No client-side permission check here: the API's refusal is the only authority. Not impersonation (nothing here touches `lib/impersonation.ts`): the operator acts as themselves on another organization. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from './api'
import type { PlatformOperator, PlatformOrganization, PlatformPerson } from './types'

const platformKeys = {
  me: ['platform', 'me'] as const,
  organizations: ['platform', 'organizations'] as const,
  people: (orgId: string) => ['platform', 'organizations', orgId, 'people'] as const,
}

/** Who the operator is. Separate from `useMe()`: the two never both succeed, since they resolve against different tables. */
export function usePlatformOperator() {
  return useQuery({
    queryKey: platformKeys.me,
    queryFn: async () => (await api.get<PlatformOperator>('/api/platform/me')).data,
    retry: false,
  })
}

export function usePlatformOrganizations() {
  return useQuery({
    queryKey: platformKeys.organizations,
    queryFn: async () =>
      (await api.get<PlatformOrganization[]>('/api/platform/organizations')).data,
  })
}

export function usePlatformOrganizationPeople(orgId: string | undefined) {
  return useQuery({
    queryKey: platformKeys.people(orgId ?? ''),
    queryFn: async () =>
      (await api.get<PlatformPerson[]>(`/api/platform/organizations/${orgId}/people`)).data,
    enabled: Boolean(orgId),
  })
}

/** The body `POST /api/organization` takes (`OrganizationCreate`). All three names and the admin address are REQUIRED server-side: every route that grants a tier is itself behind a tier, so an organization created without an admin could never be repaired from inside. The form asks for all five. */
export interface NewOrganization {
  slug: string
  name_en: string
  name_mn: string
  name_ru: string
  registration_no: string
  phone?: string
  email?: string
  mail_reply_to: string
  admin_email: string
  admin_name?: string
  entra_tenant_id?: string | null
  different_customer: boolean
}

/** The tenant, and who can now administer it. The administrator is an id and an address, not a user record. */
interface CreatedOrganization {
  organization: { id: string; slug: string; name_en: string }
  admin_person_id: string
  admin_email: string
}

export function useCreateOrganization() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (draft: NewOrganization) =>
      (await api.post<CreatedOrganization>('/api/organization', draft)).data,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: platformKeys.organizations })
    },
  })
}

/** Both admin mutations invalidate the organization list too: `admin_count` is on the list row and answers "which tenant has lost its administrator". */
function useAdminMutation<TVars, TData = unknown>(
  run: (vars: TVars) => Promise<TData>,
  orgIdOf: (vars: TVars) => string,
) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: run,
    onSuccess: (_data, vars) => {
      void client.invalidateQueries({ queryKey: platformKeys.people(orgIdOf(vars)) })
      void client.invalidateQueries({ queryKey: platformKeys.organizations })
    },
  })
}

export function useSetEntraTenant() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ orgId, tenantId }: { orgId: string; tenantId: string | null }) =>
      (
        await api.put(`/api/platform/organizations/${orgId}/entra-tenant`, {
          entra_tenant_id: tenantId,
        })
      ).data,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: platformKeys.organizations })
    },
  })
}

export function useSetOrganizationDeactivated() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ orgId, deactivated }: { orgId: string; deactivated: boolean }) =>
      (await api.put(`/api/platform/organizations/${orgId}/deactivation`, { deactivated })).data,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: platformKeys.organizations })
    },
  })
}

export function useAppointAdmin() {
  return useAdminMutation(
    async ({ orgId, personId }: { orgId: string; personId: string }) =>
      (await api.post(`/api/platform/organizations/${orgId}/admins`, { person_id: personId }))
        .data,
    (vars) => vars.orgId,
  )
}

export function useSetAdminPassword() {
  return useAdminMutation(
    async ({ orgId, personId, email }: { orgId: string; personId: string; email: string }) =>
      (
        await api.put<{ temporary_password: string }>(
          `/api/platform/organizations/${orgId}/admins/${personId}/password`,
          { email },
        )
      ).data,
    (vars) => vars.orgId,
  )
}

export function useRemoveAdmin() {
  return useAdminMutation(
    async ({ orgId, personId }: { orgId: string; personId: string }) =>
      (await api.delete(`/api/platform/organizations/${orgId}/admins/${personId}`)).data,
    (vars) => vars.orgId,
  )
}
